import React from 'react';
import { GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import {
  FSVA_COLORS, SKPG_COLORS, BORDA_DESIL_COLORS,
  NO_DATA_COLOR, getFSVACategory,
} from '../../config/ikpg';

/* ─────────── Admin boundary styles ─────────── */
const kecStyle = { color: '#c0392b', weight: 3, fillOpacity: 0, dashArray: '8,4' };
const kelStyle = { color: '#f39c12', weight: 2, fillOpacity: 0, dashArray: '4,3' };

export function KecamatanLayer({ data, onEachFeature }) {
  if (!data?.length) return null;
  return data.map((f, i) => (
    <GeoJSON key={`kec-${i}`} data={f} style={kecStyle} onEachFeature={onEachFeature} />
  ));
}

/* KelurahanLayer — supports IKPG heatmap */
function getIKPGStyle(nama, activeIKPGLayer, fsvaData, skpgData, ikpgOpacity) {
  if (!activeIKPGLayer) return kelStyle;

  if (activeIKPGLayer === 'fsva') {
    const row = fsvaData.find(r => r.nama_kelurahan === nama);
    if (!row) return { ...kelStyle, fillOpacity: 0.1, fillColor: NO_DATA_COLOR.fill };
    const { k } = getFSVACategory(parseFloat(row.ikp));
    const c = FSVA_COLORS[k] || NO_DATA_COLOR;
    return { color: c.border, weight: 1.5, fillColor: c.fill, fillOpacity: ikpgOpacity };
  }
  if (activeIKPGLayer === 'skpg') {
    const row = skpgData.find(r => r.nama_kelurahan === nama);
    if (!row) return { ...kelStyle, fillOpacity: 0.1, fillColor: NO_DATA_COLOR.fill };
    const prev = parseFloat(row.prevalensi_gizi_buruk);
    const cat = prev > 15 ? 'rentan' : prev >= 10 ? 'waspada' : 'aman';
    const c = SKPG_COLORS[cat];
    return { color: c.border, weight: 1.5, fillColor: c.fill, fillOpacity: ikpgOpacity };
  }
  if (activeIKPGLayer === 'borda') {
    const row = skpgData.find(r => r.nama_kelurahan === nama);
    if (!row?.borda_sum) return { ...kelStyle, fillOpacity: 0.1, fillColor: NO_DATA_COLOR.fill };
    // sort ascending by borda_sum to get rank
    const sorted = [...skpgData].filter(r => r.borda_sum).sort((a, b) => a.borda_sum - b.borda_sum);
    const rank = sorted.findIndex(r => r.nama_kelurahan === nama) + 1;
    const total = sorted.length;
    const desil = Math.min(10, Math.ceil((rank / total) * 10));
    const c = BORDA_DESIL_COLORS[desil] || NO_DATA_COLOR;
    return { color: c.border, weight: 1.5, fillColor: c.fill, fillOpacity: ikpgOpacity };
  }
  return kelStyle;
}

export function KelurahanLayer({
  data, onEachFeature,
  activeIKPGLayer, ikpgOpacity, fsvaData = [], skpgData = [], activeKelNames = [],
}) {
  if (!data?.length) return null;
  return data.map((f, i) => {
    const nama = f.properties?.name || f.properties?.Name || '';
    const style = getIKPGStyle(nama, activeIKPGLayer, fsvaData, skpgData, ikpgOpacity);
    return (
      <GeoJSON
        key={`kel-${i}-${activeKelNames.join()}-${activeIKPGLayer || 'x'}-${ikpgOpacity}`}
        data={f}
        style={style}
        onEachFeature={onEachFeature}
      />
    );
  });
}

export function SawahLayer({ data, showSawah, getStyle, onEachFeature, sawahStatus, fillOpacity }) {
  if (!showSawah || !data?.length) return null;
  return data.map((f, i) => (
    <GeoJSON
      key={`${f._id || i}-${JSON.stringify(sawahStatus?.[f._id] || {})}-${fillOpacity}`}
      data={f}
      style={getStyle ? getStyle(f) : undefined}
      onEachFeature={onEachFeature}
    />
  ));
}

/* ─────────── Icon helper ─────────── */
const makeIcon = (emoji, bgColor, size = 26) =>
  L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    html: `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;background:${bgColor};border:2px solid #fff;border-radius:50% 50% 50% 0;transform: scale(var(--pin-scale, 1)) rotate(-45deg);transform-origin:bottom left;box-shadow:0 2px 6px rgba(0,0,0,0.4);transition:transform 0.25s ease-out;"><span style="transform:rotate(45deg);font-size:${Math.round(size * 0.5)}px">${emoji}</span></div>`,
  });

/* ─────────── Pin layers — all use `show` prop ─────────── */
export function HortiPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('🌶️', '#52b788');
  return data.map((p, i) => (
    <GeoJSON key={`horti-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [p._lng, p._lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(`<b style="color:#52b788">🌶️ ${p._name}</b><br/>${p._komoditas || ''} ${p._pemilik ? '· 👤 ' + p._pemilik : ''}`)} />
  ));
}

export function PalawijaPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('🌿', '#74c69d');
  return data.map((p, i) => (
    <GeoJSON key={`palawija-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [p._lng, p._lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(`<b style="color:#74c69d">🌿 ${p._name}</b><br/>${p._komoditas || ''} ${p._pemilik ? '· 👤 ' + p._pemilik : ''}`)} />
  ));
}

export function PoktanPins({ data, showPoktan, showKWT, showGapoktan }) {
  if (!data?.length) return null;

  const ICONS = {
    Poktan:   makeIcon('👨‍🌾', '#2d6a4f'),
    KWT:      makeIcon('👩‍🌾', '#b5003a'),
    Gapoktan: makeIcon('🤝',   '#1a4fa0'),
  };

  return data.map((p, i) => {
    const jenis = p._jenis || 'Poktan';
    const vis = jenis === 'KWT' ? showKWT : jenis === 'Gapoktan' ? showGapoktan : showPoktan;
    if (!vis) return null;
    const icon = ICONS[jenis] || ICONS.Poktan;
    return (
      <GeoJSON key={`poktan-${i}`}
        data={{ type:'Feature', geometry:{ type:'Point', coordinates:[p._lng, p._lat] }, properties:{} }}
        pointToLayer={(_, ll) => L.marker(ll, { icon })}
        onEachFeature={(_, l) => l.bindPopup(`<b style="color:#2d6a4f">${jenis==='KWT'?'👩‍🌾':jenis==='Gapoktan'?'🤝':'👨‍🌾'} ${p._name}</b><br/>${jenis} ${p._ketua?'· Ketua: '+p._ketua:''} ${p._kelurahan?'<br/>🏘️ '+p._kelurahan:''}`)} />
    );
  });
}


export function WarningPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('⚠️', '#e63946');
  return data.map((w, i) => (
    <GeoJSON key={`warning-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [w._lng, w._lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(`<b style="color:#e63946">⚠️ ${w._name}</b><br/>${w._jenis || ''} ${w._opt ? '· 🐛 ' + w._opt : ''}`)} />
  ));
}

export function KolamPins({ data, show }) {
  if (!show || !data?.length) return null;
  return data.filter(k => k.geometry).map((k, i) => {
    const icon = makeIcon('🐟', '#0096c7');
    return (
      <GeoJSON key={`kolam-${i}`} data={k}
        style={{ color: '#0096c7', weight: 2.5, fillColor: '#0096c7', fillOpacity: 0.25 }}
        pointToLayer={(_, ll) => L.marker(ll, { icon })}
        onEachFeature={(_, l) => l.bindPopup(`<b style="color:#0096c7">🐟 ${k._name}</b><br/>${k._pemilik ? '👤 ' + k._pemilik + '<br/>' : ''}🐠 ${k._jenis_ikan || '—'}<br/>📐 ${k._luas || '—'} m² · ${k._status}`)} />
    );
  });
}

export function NelayanPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('⛵', '#2ec4b6', 28);
  return data.map((n, i) => (
    <GeoJSON key={`nelayan-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [n._lng, n._lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(`<b style="color:#2ec4b6">⛵ ${n._name}</b><br/>${n._alat ? '🎣 ' + n._alat + '<br/>' : ''}${n._jenis_ikan ? '🐟 ' + n._jenis_ikan + '<br/>' : ''}${n._perahu ? 'Perahu: ' + n._perahu : ''}`)} />
  ));
}

export function KolamDBPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('🐟', '#0096c7');
  return data.filter(r => r.lat && r.lng).map((r, i) => (
    <GeoJSON key={`kolam-db-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lng, r.lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(`<b style="color:#0096c7">🐟 ${r.nama_pemilik || '—'}</b><br/>🐠 ${r.jenis_ikan}<br/>📐 ${r.luas_m2 ? r.luas_m2 + ' m²' : '—'} · ${r.status_kolam}`)} />
  ));
}

/* ─── DB Nelayan Pins (from nelayan_tangkap table) ─── */
export function NelayanDBPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('⛵', '#2ec4b6', 28);
  return data.filter(r => r.lat && r.lng && r.lat !== 0).map((r, i) => (
    <GeoJSON key={`nelayan-db-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lng, r.lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(`<b style="color:#2ec4b6">⛵ ${r.nama_nelayan || '—'}</b><br/>🎣 ${r.alat_tangkap || '—'}<br/>👥 ${r.no_hp || '?'} orang`)} />
  ));
}

/* ─── DB Poktan/KWT/Gapoktan Pins ─── */
export function PoktanDBPins({ data, showPoktan, showKWT, showGapoktan }) {
  if (!data?.length) return null;
  const ICONS = {
    Poktan:   makeIcon('👨‍🌾', '#2d6a4f'),
    KWT:      makeIcon('👩‍🌾', '#b5003a'),
    Gapoktan: makeIcon('🤝',   '#1a4fa0'),
  };
  return data.filter(r => r.lat && r.lng && r.lat !== 0).map((r, i) => {
    const jenis = r.jenis || 'Poktan';
    const vis = jenis === 'KWT' ? showKWT : jenis === 'Gapoktan' ? showGapoktan : showPoktan;
    if (!vis) return null;
    const icon = ICONS[jenis] || ICONS.Poktan;
    const produk = r.produk_unggulan ? `<br/>🌾 ${r.produk_unggulan}` : '';
    const status = r.status_aktif ? `<br/><span style="color:${r.status_aktif==='Aktif'?'#2d6a4f':'#888'}">${r.status_aktif}</span>` : '';
    return (
      <GeoJSON key={`poktan-db-${i}`}
        data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lng, r.lat] }, properties: {} }}
        pointToLayer={(_, ll) => L.marker(ll, { icon })}
        onEachFeature={(_, l) => l.bindPopup(
          `<b style="color:#2d6a4f">${jenis==='KWT'?'👩‍🌾':jenis==='Gapoktan'?'🤝':'👨‍🌾'} ${r.nama_poktan}</b><br/>${jenis}${r.nama_ketua ? ' · Ketua: '+r.nama_ketua : ''}${r.kelurahan ? '<br/>🏘️ '+r.kelurahan : ''}${produk}${status}`
        )} />
    );
  });
}

/* ─── DB Hortikultura Pins ─── */
export function HortiDBPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('🌶️', '#52b788');
  return data.filter(r => r.lat && r.lon).map((r, i) => (
    <GeoJSON key={`horti-db-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lon, r.lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(
        `<b style="color:#52b788">🌶️ ${r.nama_pemilik || r.komoditas || '—'}</b><br/>${r.komoditas || ''}${r.tanggal_tanam ? '<br/>📅 Tanam: '+r.tanggal_tanam : ''}${r.prediksi_panen ? '<br/>🌾 Est. Panen: '+r.prediksi_panen : ''}`
      )} />
  ));
}

/* ─── DB Palawija Pins ─── */
export function PalawijaDBPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('🌿', '#74c69d');
  return data.filter(r => r.lat && r.lon && r.lat !== 0).map((r, i) => (
    <GeoJSON key={`palawija-db-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lon, r.lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(
        `<b style="color:#74c69d">🌿 ${r.nama_pemilik || r.komoditas || '—'}</b><br/>${r.komoditas || ''}${r.tanggal_tanam ? '<br/>📅 Tanam: '+r.tanggal_tanam : ''}${r.prediksi_panen ? '<br/>🌾 Est. Panen: '+r.prediksi_panen : ''}`
      )} />
  ));
}


/* ─── DB Warning OPT Pins ─── */
export function WarningDBPins({ data, show }) {
  if (!show || !data?.length) return null;
  const icon = makeIcon('⚠️', '#e63946');
  return data.filter(r => r.lat && r.lat !== 0).map((r, i) => (
    <GeoJSON key={`warning-db-${i}`}
      data={{ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lng, r.lat] }, properties: {} }}
      pointToLayer={(_, ll) => L.marker(ll, { icon })}
      onEachFeature={(_, l) => l.bindPopup(
        `<b style="color:#e63946">⚠️ ${r.nama_opt || r.jenis_warning || 'Warning'}</b><br/>📍 ${r.kelurahan || '—'}${r.luas_terdampak ? '<br/>Luas: '+r.luas_terdampak+' '+r.satuan_luas : ''}`
      )} />
  ));
}